import { PDFDocumentProxy } from "pdfjs-dist";
import type { LTWHP, ScaledPosition } from "../types";
import { viewportToScaled } from "./coordinates";
import getBoundingRect from "./get-bounding-rect";
import optimizeClientRects from "./optimize-client-rects";

interface TextSearchMatch {
  pageNumber: number;
  position: ScaledPosition;
}

/**
 * Searches the PDF text layer DOM for a given string and returns
 * ScaledPosition objects compatible with the existing highlight system.
 * If a partial match is found, the entire containing paragraph is highlighted.
 */
export async function findTextInPdf(
  searchText: string,
  pdfDocument: PDFDocumentProxy,
  viewer: any
): Promise<TextSearchMatch[]> {
  const matches: TextSearchMatch[] = [];
  if (!searchText || searchText.trim().length === 0) return matches;

  const numPages = pdfDocument.numPages;

  for (let pageNum = 1; pageNum <= numPages; pageNum++) {
    const pageView = viewer.getPageView(pageNum - 1);
    if (!pageView?.textLayer?.div) continue;

    const textLayerDiv = pageView.textLayer.div as HTMLElement;
    const pageNode = pageView.div as HTMLElement;
    const { viewport } = pageView;

    const pageMatches = findMatchesInTextLayer(
      searchText,
      textLayerDiv,
      pageNode,
      pageNum,
      viewport
    );

    matches.push(...pageMatches);
  }

  return matches;
}

interface SpanEntry {
  span: HTMLElement;
  textNode: Text;
  index: number; // index in the spans array
}

/**
 * For each character in the normalized text, track which span it belongs to.
 */
interface CharEntry {
  node: Text;
  offset: number;
  spanIndex: number;
}

function collectSpanEntries(spanElements: HTMLElement[]): SpanEntry[] {
  const spanEntries: SpanEntry[] = [];
  for (let idx = 0; idx < spanElements.length; idx++) {
    const textNode = getTextNode(spanElements[idx]);
    if (!textNode) continue;
    spanEntries.push({ span: spanElements[idx], textNode, index: idx });
  }
  return spanEntries;
}

function shouldCollapseWhitespace(normalizedText: string): boolean {
  return (
    normalizedText.length === 0 ||
    normalizedText[normalizedText.length - 1] === " "
  );
}

function buildCharMapAndNormalizedText(spanEntries: SpanEntry[]): {
  charMap: CharEntry[];
  normalizedText: string;
} {
  const charMap: CharEntry[] = [];
  let normalizedText = "";

  for (const { textNode, index } of spanEntries) {
    const text = textNode.textContent || "";
    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      const isWhitespace = /\s/.test(ch);

      if (isWhitespace && shouldCollapseWhitespace(normalizedText)) {
        continue;
      }

      charMap.push({ node: textNode, offset: i, spanIndex: index });
      normalizedText += isWhitespace ? " " : ch;
    }
  }

  return { charMap, normalizedText };
}

function findExactMatches(
  lowerFull: string,
  lowerSearch: string,
  charMap: CharEntry[],
  spanElements: HTMLElement[],
  pageNode: HTMLElement,
  pageNumber: number,
  viewport: { width: number; height: number }
): { matches: TextSearchMatch[]; foundTextMatch: boolean } {
  const matches: TextSearchMatch[] = [];
  let foundTextMatch = false;
  let startIdx = 0;

  while (startIdx < lowerFull.length) {
    const matchIdx = lowerFull.indexOf(lowerSearch, startIdx);
    if (matchIdx === -1) break;
    foundTextMatch = true;

    const endIdx = matchIdx + lowerSearch.length - 1;
    const matchStartSpan = charMap[matchIdx].spanIndex;
    const matchEndSpan = charMap[endIdx].spanIndex;

    const paraMatch = expandToParagraph(
      spanElements,
      matchStartSpan,
      matchEndSpan,
      charMap,
      pageNode,
      pageNumber,
      viewport
    );

    if (paraMatch) matches.push(paraMatch);
    startIdx = matchIdx + lowerSearch.length;
  }

  return { matches, foundTextMatch };
}

function findMatchesInTextLayer(
  searchText: string,
  textLayerDiv: HTMLElement,
  pageNode: HTMLElement,
  pageNumber: number,
  viewport: { width: number; height: number }
): TextSearchMatch[] {
  const spanElements = Array.from(
    textLayerDiv.querySelectorAll("span[role='presentation']")
  ) as HTMLElement[];

  if (spanElements.length === 0) return [];

  const spanEntries = collectSpanEntries(spanElements);
  const { charMap, normalizedText } =
    buildCharMapAndNormalizedText(spanEntries);

  const lowerFull = normalizedText.toLowerCase();
  const lowerSearch = searchText.toLowerCase().replace(/\s+/g, " ").trim();

  const { matches, foundTextMatch } = findExactMatches(
    lowerFull,
    lowerSearch,
    charMap,
    spanElements,
    pageNode,
    pageNumber,
    viewport
  );

  if (!foundTextMatch) {
    const chunkMatch = findByChunks(
      lowerFull,
      lowerSearch,
      charMap,
      spanElements,
      pageNode,
      pageNumber,
      viewport
    );
    if (chunkMatch) matches.push(chunkMatch);
  }

  return matches;
}

/**
 * Given a match spanning from matchStartSpan to matchEndSpan,
 * expand outward to include the full paragraph. A paragraph boundary
 * is detected when the vertical gap between consecutive spans is
 * significantly larger than the line height of the matched text.
 */
function expandToParagraph(
  allSpans: HTMLElement[],
  matchStartSpan: number,
  matchEndSpan: number,
  charMap: CharEntry[],
  pageNode: HTMLElement,
  pageNumber: number,
  viewport: { width: number; height: number }
): TextSearchMatch | null {
  // Get the line height from the matched spans
  const matchedRect = allSpans[matchStartSpan].getBoundingClientRect();
  const lineHeight = matchedRect.height;
  // Paragraph break = gap > 1.5x line height
  const gapThreshold = lineHeight * 1.5;

  // Expand backward
  let paraStart = matchStartSpan;
  for (let i = matchStartSpan - 1; i >= 0; i--) {
    const currTop = allSpans[i + 1].getBoundingClientRect().top;
    const prevTop = allSpans[i].getBoundingClientRect().top;
    const gap = Math.abs(currTop - prevTop);
    if (gap > gapThreshold) break;
    paraStart = i;
  }

  // Expand forward
  let paraEnd = matchEndSpan;
  for (let i = matchEndSpan + 1; i < allSpans.length; i++) {
    const currTop = allSpans[i - 1].getBoundingClientRect().top;
    const nextTop = allSpans[i].getBoundingClientRect().top;
    const gap = Math.abs(nextTop - currTop);
    if (gap > gapThreshold) break;
    paraEnd = i;
  }

  // Find first and last chars in charMap that belong to these spans
  let firstCharIdx = -1;
  let lastCharIdx = -1;
  for (let i = 0; i < charMap.length; i++) {
    if (charMap[i].spanIndex >= paraStart && firstCharIdx === -1) {
      firstCharIdx = i;
    }
    if (charMap[i].spanIndex <= paraEnd) {
      lastCharIdx = i;
    }
  }

  if (firstCharIdx === -1 || lastCharIdx === -1) return null;

  const range = document.createRange();
  range.setStart(charMap[firstCharIdx].node, charMap[firstCharIdx].offset);
  range.setEnd(charMap[lastCharIdx].node, charMap[lastCharIdx].offset + 1);

  const rects = extractRectsFromRange(range, pageNode, pageNumber);
  range.detach();

  if (rects.length === 0) return null;

  const boundingRect = getBoundingRect(rects);
  const scaledPosition: ScaledPosition = {
    boundingRect: viewportToScaled(boundingRect, viewport),
    rects: rects.map((rect) => viewportToScaled(rect, viewport)),
    pageNumber
  };

  return { pageNumber, position: scaledPosition };
}

/**
 * Chunk-based fallback: splits search text into 2-word chunks, finds which
 * spans contain the most chunk hits, then expands that region to its paragraph.
 * Requires >= 3 chunk hits and >= 50% of chunks matching.
 */
function findByChunks(
  fullText: string,
  searchText: string,
  charMap: CharEntry[],
  allSpans: HTMLElement[],
  pageNode: HTMLElement,
  pageNumber: number,
  viewport: { width: number; height: number }
): TextSearchMatch | null {
  const words = searchText.split(" ").filter((w) => w.length > 2);
  if (words.length < 3) return null;

  const chunks: string[] = [];
  for (let i = 0; i < words.length - 1; i++) {
    chunks.push(words[i] + " " + words[i + 1]);
  }

  // Find position of each chunk in the full text
  const hitPositions: number[] = [];
  let hitCount = 0;
  for (const chunk of chunks) {
    const idx = fullText.indexOf(chunk);
    if (idx !== -1) {
      hitCount++;
      hitPositions.push(idx);
    }
  }

  if (hitCount < 3 || hitCount < chunks.length * 0.5) return null;

  // Use the median hit position to anchor the match
  hitPositions.sort((a, b) => a - b);
  const medianPos = hitPositions[Math.floor(hitPositions.length / 2)];

  const anchorSpan = charMap[medianPos]?.spanIndex;
  if (anchorSpan === undefined) return null;

  return expandToParagraph(
    allSpans,
    anchorSpan,
    anchorSpan,
    charMap,
    pageNode,
    pageNumber,
    viewport
  );
}

function getTextNode(element: HTMLElement): Text | null {
  for (const child of Array.from(element.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      return child as Text;
    }
  }
  return null;
}

function extractRectsFromRange(
  range: Range,
  pageNode: HTMLElement,
  pageNumber: number
): LTWHP[] {
  const clientRects = Array.from(range.getClientRects());
  const pageRect = pageNode.getBoundingClientRect();
  const rects: LTWHP[] = [];

  for (const clientRect of clientRects) {
    if (
      clientRect.width > 0 &&
      clientRect.height > 0 &&
      clientRect.width < pageRect.width &&
      clientRect.height < pageRect.height
    ) {
      rects.push({
        top: clientRect.top + pageNode.scrollTop - pageRect.top,
        left: clientRect.left + pageNode.scrollLeft - pageRect.left,
        width: clientRect.width,
        height: clientRect.height,
        pageNumber
      });
    }
  }

  return optimizeClientRects(rects);
}

/** @internal Exported for unit tests only */
export const textSearchHighlightInternals = {
  collectSpanEntries,
  buildCharMapAndNormalizedText,
  findExactMatches,
  expandToParagraph,
  findByChunks,
  getTextNode
};
